import React, { useEffect, useState } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import Categorys from "../components/Categorys";
import SEO from "../components/SEO";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useDispatch, useSelector } from "react-redux";
import "swiper/css/pagination";
import { Pagination } from "swiper";
import Ratings from "../components/Ratings";
import { AiFillHeart } from "react-icons/ai";
import { FaFacebookF, FaLinkedin, FaWhatsapp, FaShareAlt } from "react-icons/fa";
import { AiFillGithub, AiOutlineTwitter } from "react-icons/ai";
import Reviews from "../components/Reviews";

import { get_product, get_banners, get_category, clearProductData, updateProductData } from "../store/reducers/homeReducer";
import {
  add_to_card,
  messageClear,
  add_to_wishlist,
} from "../store/reducers/cardReducer";
import toast from "react-hot-toast";
import BulkPricingComponent from "./BulkPricingComponent";
import { saveProductToSession, getGuestSessionId } from "../utils/cartUtils";
import { getProxiedUrl } from '../utils/imageUtils'
import { getDisplayPrice } from '../utils/priceCalculator'

// Utility function to convert URLs in text to clickable links
const convertUrlsToLinks = (text) => {
  if (!text) return '';
  
  // Regex to match URLs (http, https, www, etc.)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  
  return text.split(urlRegex).map((part, index) => {
    if (urlRegex.test(part)) {
      const url = part.startsWith('www.') ? `https://${part}` : part;
      return (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Separate component for product images
const ProductImages = React.memo(({ product, isLoading, onImageSelect, selectedImage }) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [timestamp] = useState(Date.now());

  // Function to get the best image for display
  const getBestImage = (product) => {
    if (!product?.images || product.images.length === 0) {
      return '/images/no_image.jpg';
    }

    // If product has brand and we're looking for barcode image
    if (product.brand) {
      const barcodeImage = product.images.find(img => 
        img && img.includes(`${product.brand}.gif`)
      );
      if (barcodeImage) {
        return barcodeImage;
      }
    }

    // Fallback to first image
    return product.images[0];
  };

  // Get the current image to display - use selectedImage if available, otherwise get best image
  const getCurrentImage = () => {
    if (selectedImage) {
      return selectedImage;
    }
    return getBestImage(product);
  };

  // Initialize selected image when product changes
  useEffect(() => {
    if (product?.images && product.images.length > 0 && !selectedImage) {
      const bestImage = getBestImage(product);
      if (onImageSelect) {
        onImageSelect(bestImage);
      }
    }
  }, [product, onImageSelect, selectedImage]);

  if (isLoading || !product || !product.name || !product.price) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="relative w-full h-[400px] overflow-hidden rounded-lg mb-4">
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="relative w-full h-[400px] overflow-hidden rounded-lg mb-4">
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/50 backdrop-blur-sm z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        )}
        <img
          className="w-full h-full object-contain"
          src={getProxiedUrl(getCurrentImage())}
          alt={product?.name || "Product image"}
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageLoading(false);
            if (onImageSelect) {
              onImageSelect("/images/no_image.jpg");
            }
          }}
        />
        {product?.discount > 0 && (
          <div className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium">
            -{product.discount}% OFF
          </div>
        )}
      </div>
      {product?.images && product.images.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          {product.images.map((img, i) => (
            <div 
              key={`thumbnail-${i}-${timestamp}`}
              onClick={() => {
                setImageLoading(true);
                // Notify parent component of the selected image
                if (onImageSelect) {
                  onImageSelect(img);
                }
              }}
              className={`cursor-pointer border-2 rounded-lg overflow-hidden ${
                getCurrentImage() === img ? "border-indigo-500" : "border-gray-100"
              }`}
            >
              <img
                className="w-full h-24 object-contain"
                src={getProxiedUrl(img)}
                alt=""
                onError={(e) => {
                  e.target.src = "/images/no_image.jpg";
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const Details = ({ sellerId, sellerName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug, productId } = useParams();
  const { userInfo, supportInfo, seller } = useSelector((state) => state.auth);
  const { product, relatedProducts, moreProducts, banners, totalReview, isLoading, isProductLoading } = useSelector(
    (state) => state.home
  );
  const { card_product_count, errorMessage, successMessage } = useSelector((state) => state.card);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [bulkPrice, setBulkPrice] = useState(null);
  const [selectedBulkPricing, setSelectedBulkPricing] = useState(null);
  const [bulkMultiplier, setBulkMultiplier] = useState(1); // New state for bulk multiplier
  const [state, setState] = useState("reviews");
  const [currentPrice, setCurrentPrice] = useState(0);
  const [selectedImage, setSelectedImage] = useState("");

  // Set document title
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 5,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 5,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 4,
    },
    mdtablet: {
      breakpoint: { max: 991, min: 464 },
      items: 4,
    },
    mobile: {
      breakpoint: { max: 768, min: 0 },
      items: 3,
    },
    smmobile: {
      breakpoint: { max: 640, min: 0 },
      items: 2,
    },
    xsmobile: {
      breakpoint: { max: 440, min: 0 },
      items: 1,
    },
  };

  const responsiveBanner = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  const inc = () => {
    if (!product) return;
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    setSelectedBulkPricing(null); // Clear bulk pricing selection when manually changing quantity
    setBulkMultiplier(1); // Reset bulk multiplier
    const newTotalPrice = calculateTotalPrice();
    setCurrentPrice(newTotalPrice);
  };

  const dec = () => {
    if (!product) return;
    
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      setSelectedBulkPricing(null); // Clear bulk pricing selection when manually changing quantity
      setBulkMultiplier(1); // Reset bulk multiplier
      
      // Update current price based on new quantity
      const newTotalPrice = calculateTotalPrice();
      setCurrentPrice(newTotalPrice);
    }
  };


  const add_card = () => {
    if (!product) {
      toast.error("Product information is not available");
      return;
    }

    // Calculate final quantity based on bulk pricing selection
    let currentQuantity;
    if (selectedBulkPricing) {
      // When bulk pricing is selected, use multiplier * bulk pack quantity
      const multiplier = typeof bulkMultiplier === 'string' ? parseInt(bulkMultiplier, 10) || 1 : bulkMultiplier;
      currentQuantity = multiplier * selectedBulkPricing.quantity;
    } else {
      // Regular quantity when no bulk pricing is selected
      currentQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) || 1 : quantity;
    }
    
    if (currentQuantity < 1) {
      toast.error("Please enter a valid quantity");
      return;
    }

    // Allow adding to cart even if out of stock

    // Process selected addons
    const selectedAddonOptions = Object.entries(selectedAddons).reduce((acc, [addonIndex, addon]) => {
      if (Array.isArray(addon)) {
        // Handle checkbox addons (multiple selections)
        return [...acc, ...addon];
      } else if (addon && addon.price > 0) {
        // Handle single selection addons (dropdown and radio)
        return [...acc, addon];
      }
      return acc;
    }, []);

    if (userInfo) {
      dispatch(
        add_to_card({
          userId: userInfo.id,
          quantity: currentQuantity,
          productId: product._id,
          addons: selectedAddonOptions,
          price: product.price,
          bulkPricing: product.bulkPricing,
          discountPrice: calculateTotalPrice(),
          sellerId: sellerId,
          packSize: product.packSize,
          selectedImage: selectedImage,
        })
      );
    } else {
      // For guest users, add to cart directly
      dispatch(
        add_to_card({
          userId: null,
          quantity: currentQuantity,
          productId: product._id,
          addons: selectedAddonOptions,
          price: product.price,
          bulkPricing: product.bulkPricing,
          discountPrice: calculateTotalPrice(),
          sellerId: sellerId,
          packSize: product.packSize,
          selectedImage: selectedImage,
        })
      );
    }
  };

  const updateCurrentPrice = (price) => {
    setCurrentPrice(price);
  };

  // Calculate total price based on quantity, bulk pricing, addons, and discount
  const calculateTotalPrice = () => {
    if (!product) return 0;

    // Calculate final quantity based on bulk pricing selection
    let currentQuantity;
    if (selectedBulkPricing) {
      // When bulk pricing is selected, use multiplier * bulk pack quantity
      const multiplier = typeof bulkMultiplier === 'string' ? parseInt(bulkMultiplier, 10) || 1 : bulkMultiplier;
      currentQuantity = multiplier * selectedBulkPricing.quantity;
    } else {
      // Regular quantity when no bulk pricing is selected
      currentQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) || 1 : quantity;
    }
    
    if (currentQuantity < 1) return 0;

    let basePrice = product.price;
    
    // Apply discount if available
    if (product.discount > 0) {
      const discountAmount = (basePrice * product.discount) / 100;
      basePrice = basePrice - discountAmount;
    }

    // Calculate total price based on whether bulk pricing is selected or not
    let totalPrice = 0;
    
    if (selectedBulkPricing) {
      // When bulk pricing is selected, multiply the bulk price by the multiplier
      const multiplier = typeof bulkMultiplier === 'string' ? parseInt(bulkMultiplier, 10) || 1 : bulkMultiplier;
      totalPrice = selectedBulkPricing.price * multiplier;
    } else if (product.bulkPricing && Array.isArray(product.bulkPricing)) {
      // Bulk tier logic: apply tier per-item rate to the full selected quantity
      const validBulkPricing = product.bulkPricing.filter(
        (item) => item.quantity !== null && item.price !== null
      );
      
      if (validBulkPricing.length > 0) {
        const sortedBulkPricing = [...validBulkPricing].sort(
          (a, b) => (b.quantity || 0) - (a.quantity || 0)
        );
        const matchedTier = sortedBulkPricing.find(
          (bulkOption) => currentQuantity >= bulkOption.quantity
        );

        if (matchedTier) {
          const tierUnitPrice = Number(matchedTier.price) / Number(matchedTier.quantity);
          totalPrice = tierUnitPrice * currentQuantity;
        } else {
          totalPrice = basePrice * currentQuantity;
        }
      } else {
        // No bulk pricing, use regular price
        totalPrice = basePrice * currentQuantity;
      }
    } else {
      // No bulk pricing, use regular price
      totalPrice = basePrice * currentQuantity;
    }

    // Calculate addons total
    let addonsTotal = 0;
    Object.entries(selectedAddons).forEach(([addonIndex, addon]) => {
      if (Array.isArray(addon)) {
        // Handle checkbox addons (multiple selections)
        addon.forEach(option => {
          addonsTotal += option.price || 0;
        });
      } else if (addon && addon.price > 0) {
        // Handle single selection addons
        addonsTotal += addon.price;
      }
    });

    // Add-ons apply per unit: (sum of selected add-on prices) × quantity
    totalPrice += addonsTotal * currentQuantity;
    
    return totalPrice;
  };

  const add_wishlist = () => {
    if (userInfo) {
      dispatch(
        add_to_wishlist({
          userId: userInfo.id,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
          currency: product.currency,
        })
      );
    } else {
      // For guest users, add to wishlist using sessionId
      const sessionId = getGuestSessionId();
      
      dispatch(
        add_to_wishlist({
          sessionId: sessionId,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
          currency: product.currency,
        })
      );
    }
  };

  // Reset all states
  const resetStates = () => {
    setSelectedAddons([]);
    setQuantity(1);
    setCurrentPrice(0);
    setSelectedBulkPricing(null);
    setBulkMultiplier(1);
  };

  // Effect for loading product data
  useEffect(() => {
    // Only load product if we have the required parameters
    if (!sellerId || (!productId && !slug)) {
      return;
    }

    // Check if we already have the correct product loaded
    const currentProductIdentifier = productId || slug;
    const loadedProductIdentifier = product?._id || product?.slug;
    
    // If we already have the correct product loaded, don't reload
    if (loadedProductIdentifier && 
        (currentProductIdentifier === loadedProductIdentifier || 
         currentProductIdentifier === product?._id)) {
      return;
    }

    // Load the product
    if (productId) {
      dispatch(get_product({ productId, sellerId }));
    } else if (slug) {
      dispatch(get_product({ slug, sellerId }));
    }
  }, [slug, productId, sellerId, dispatch]);

  // Effect for handling product data changes
  useEffect(() => {
    if (product && !isLoading) {
      // Calculate initial price with discount if applicable
      let initialPrice = product.price;
      if (product.discount > 0) {
        const discountAmount = (initialPrice * product.discount) / 100;
        initialPrice = initialPrice - discountAmount;
      }
      setCurrentPrice(initialPrice);
    }
  }, [product, isLoading]);



  useEffect(() => {
    dispatch(get_banners(sellerId));
    dispatch(get_category(sellerId));
  }, [sellerId, dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch]);

  // Initialize current price when product loads
  useEffect(() => {
    if (product) {
      const initialPrice = calculateTotalPrice();
      setCurrentPrice(initialPrice);
    }
  }, [product]);

  // Reset product-related state when product changes
  useEffect(() => {
    if (product && product._id) {
      setSelectedImage(""); // Reset selected image
      setSelectedAddons([]); // Reset selected addons
      setSelectedVariants({}); // Reset selected variants
      setQuantity(1); // Reset quantity to 1
      setActiveImage(0); // Reset active image index
      setBulkPrice(null); // Reset bulk price
      setSelectedBulkPricing(null); // Reset bulk pricing selection
      setBulkMultiplier(1); // Reset bulk multiplier
    }
  }, [product?._id]); // Only reset when product ID changes

  const handleAddonChange = (addonIndex, option) => {
    const newSelectedAddons = [...selectedAddons];
    
    // Check if this is a checkbox addon (multiple selections allowed)
    const currentAddon = newSelectedAddons[addonIndex];
    if (Array.isArray(currentAddon)) {
      // Handle checkbox addons - toggle the option
      const optionIndex = currentAddon.findIndex(item => item.text === option.text);
      if (optionIndex >= 0) {
        // Remove the option if it exists
        currentAddon.splice(optionIndex, 1);
      } else {
        // Add the option if it doesn't exist
        currentAddon.push(option);
      }
    } else {
      // Handle single selection addons (radio, dropdown)
      newSelectedAddons[addonIndex] = option;
    }
    
    setSelectedAddons(newSelectedAddons);
    
    // Update current price when addons change
    const newTotalPrice = calculateTotalPrice();
    setCurrentPrice(newTotalPrice);
  };

  const handleVariantChange = (variantName, value) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantName]: value,
    }));
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input for user to clear and type
    if (value === '') {
      setQuantity('');
      setSelectedBulkPricing(null); // Clear bulk pricing selection when manually changing quantity
      setBulkMultiplier(1); // Reset bulk multiplier
      return;
    }
    
    // Only allow positive integers
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantity(numValue);
      setSelectedBulkPricing(null); // Clear bulk pricing selection when manually changing quantity
      setBulkMultiplier(1); // Reset bulk multiplier
      const newTotalPrice = calculateTotalPrice();
      setCurrentPrice(newTotalPrice);
    }
  };

  // Handle quantity input blur (when user finishes typing)
  const handleQuantityBlur = () => {
    // If quantity is empty or invalid, reset to 1
    if (!quantity || quantity < 1) {
      setQuantity(1);
      const newTotalPrice = calculateTotalPrice();
      setCurrentPrice(newTotalPrice);
    }
  };

  // Handle bulk pricing selection
  const handleBulkPricingSelect = (bulkOption) => {
    // If the same bulk option is clicked again, clear the selection
    if (selectedBulkPricing && selectedBulkPricing.quantity === bulkOption.quantity) {
      setSelectedBulkPricing(null);
      setBulkMultiplier(1);
      setQuantity(1);
    } else {
      setSelectedBulkPricing(bulkOption);
      // Set quantity to 1 initially, user can then enter multiplier
      setQuantity(1);
      setBulkMultiplier(1);
    }
    const newTotalPrice = calculateTotalPrice();
    setCurrentPrice(newTotalPrice);
  };

  // Handle bulk multiplier change
  const handleBulkMultiplierChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input for user to clear and type
    if (value === '') {
      setBulkMultiplier('');
      return;
    }
    
    // Only allow positive integers
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setBulkMultiplier(numValue);
      const newTotalPrice = calculateTotalPrice();
      setCurrentPrice(newTotalPrice);
    }
  };

  // Handle bulk multiplier input blur
  const handleBulkMultiplierBlur = () => {
    // If multiplier is empty or invalid, reset to 1
    if (!bulkMultiplier || bulkMultiplier < 1) {
      setBulkMultiplier(1);
      const newTotalPrice = calculateTotalPrice();
      setCurrentPrice(newTotalPrice);
    }
  };

  const handleAddToCart = () => {
    add_card();
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = product?.name;
    const text = `Check out ${product?.name} on our store!`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url).then(() => {
          toast.success('Link copied to clipboard!');
        }).catch(() => {
          toast.error('Failed to copy link');
        });
        break;
      default:
        break;
    }
  };

  // Format phone number for WhatsApp
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Get country code from seller info, default to '27' for South Africa if not available
    const countryCode = seller?.countryCode || '27';
    
    // If number starts with 0, replace with country code
    if (cleaned.startsWith('0')) {
      return countryCode + cleaned.substring(1);
    }
    // If number doesn't start with country code, add it
    if (!cleaned.startsWith(countryCode)) {
      return countryCode + cleaned;
    }
    return cleaned;
  };

  // Debounced Not Found: keep a small grace period before showing Not Found
  const [notFoundDelayPassed, setNotFoundDelayPassed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setNotFoundDelayPassed(true), 600);
    return () => clearTimeout(t);
  }, [slug, productId]);

  // Show loading state while product is loading
  if (isProductLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading product details...</p>
        </div>
      </div>
    );
  }

  // If not loading and product is missing/empty, show Not Found
  if ((!product || Object.keys(product).length === 0) && notFoundDelayPassed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-4">The product you're looking for doesn't exist or has been removed.</p>
          <Link
            to={`/${sellerName}/shops`}
            className="inline-block px-6 py-3 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
          >
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  // Check if product data is actually populated with meaningful content
  if (!product.name || !product.price) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-4">The product you're looking for doesn't exist or has been removed.</p>
          <Link
            to={`/${sellerName}/shops`}
            className="inline-block px-6 py-3 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
          >
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div key={`${sellerName}-${productId || slug}`}>
      {/* SEO Component */}
      {product && product.name && product.price && product._id && (
        <SEO
          product={product}
          url={window.location.href}
          type="product"
          image={product.images && product.images.length > 0 ? getProxiedUrl(product.images[0]) : "/images/logo.png"}
          breadcrumbs={[
            { name: "Home", url: `/${sellerName}` },
            { name: product.category || "Products", url: `/${sellerName}/shops` },
            { name: product.name, url: window.location.href }
          ]}
        />
      )}
      
      {/* Final safety check - ensure all required data is present */}
      {(!product || !product.name || !product.price || !product._id) && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading product details...</p>
          </div>
        </div>
      )}
      
      {/* Only render content when all data is ready */}
      {product && product.name && product.price && product._id && (
        <>
          <Headers sellerId={sellerId} sellerName={sellerName} />
          
          {/* Category Section */}
          <div className="my-4">
            <Categorys sellerId={sellerId} sellerName={sellerName} />
          </div>
          
          <div className="bg-gray-50 py-8">
            <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] mx-auto">
              <div className="flex flex-wrap gap-8">
                {/* Product Images */}
                <div className="w-[45%] md-lg:w-full">
                  <ProductImages 
                    key={`product-images-${sellerId}-${productId || slug}`}
                    product={product}
                    isLoading={isLoading}
                    selectedImage={selectedImage}
                    onImageSelect={(image) => {
                      if (product?.images && Array.isArray(product.images)) {
                        setActiveImage(product.images.indexOf(image));
                      }
                      setSelectedImage(image);
                    }}
                  />
                </div>

                {/* Product Info */}
                <div className="w-[50%] md-lg:w-full">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                      {product?.name}
                    </h2>
                    
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-400">★</span>
                        <span className="text-gray-600">{product?.rating || 0}</span>
                      </div>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-600">{totalReview} Reviews</span>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                      <div>
                        {(() => {
                          const currentQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) || 1 : quantity;
                          const multiplier = typeof bulkMultiplier === 'string' ? parseInt(bulkMultiplier, 10) || 1 : bulkMultiplier;

                          // When a bulk option is selected, show that option's price and quantity
                          if (selectedBulkPricing) {
                            const packPrice = Number(selectedBulkPricing.price);
                            const packQty = Number(selectedBulkPricing.quantity);
                            const perItem = packQty > 0 ? packPrice / packQty : packPrice;
                            const totalForSelection = calculateTotalPrice();
                            const effectiveQty = multiplier * packQty;
                            return (
                              <>
                                <span className="text-2xl font-bold text-indigo-600">
                                  {product?.currency} {packPrice.toFixed(2)}
                                  <span className="text-lg font-semibold text-gray-700 ml-1">for {packQty}</span>
                                </span>
                                <div className="text-sm text-gray-500 mt-1">
                                  {product?.currency} {perItem.toFixed(2)} per item
                                </div>
                                {multiplier > 1 && (
                                  <div className="text-sm font-medium text-gray-700 mt-1">
                                    {multiplier} × {packQty} = {effectiveQty} units
                                  </div>
                                )}
                                <div className="text-sm font-medium text-gray-700 mt-1">
                                  Total: {product?.currency} {totalForSelection.toFixed(2)}
                                </div>
                              </>
                            );
                          }

                          // No bulk selected: show main price (max tier if bulk exists, else regular/discounted)
                          const display = getDisplayPrice(product);
                          const showRunningTotal = currentQuantity > 1;
                          return (
                            <>
                              <span className="text-2xl font-bold text-indigo-600">
                                {display.currency} {Number(display.price).toFixed(2)}
                                {display.isBulk && (
                                  <span className="text-lg font-semibold text-gray-700 ml-1">for {display.quantity}</span>
                                )}
                              </span>
                              {display.isBulk && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {display.currency} {Number(display.perItemPrice).toFixed(2)} per item
                                </div>
                              )}
                              {!display.isBulk && currentQuantity > 1 && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {product?.currency} {(calculateTotalPrice() / currentQuantity).toFixed(2)} per item
                                </div>
                              )}
                              {showRunningTotal && (
                                <div className="text-sm font-medium text-gray-700 mt-1">
                                  Total: {product?.currency} {calculateTotalPrice().toFixed(2)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {!selectedBulkPricing && !getDisplayPrice(product).isBulk && product?.discount > 0 && (
                        <span className="text-lg text-gray-500 line-through">
                          {product?.currency} {(product?.price * (typeof quantity === 'string' ? parseInt(quantity, 10) || 1 : quantity)).toFixed(2)}
                        </span>
                      )}
                    </div>

                    {(product?.showStockQuantity !== false || product?.stock <= 0) && (
                      <div className="flex items-center gap-2 mb-6">
                        <span className={`w-3 h-3 rounded-full ${product?.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span className="text-base text-gray-700 font-medium">
                          {product?.stock > 0
                            ? (product?.showStockQuantity ? `In stock: ${product.stock}` : 'In stock')
                            : 'Out of stock'}
                        </span>
                      </div>
                    )}

                    <div className="text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
                      {convertUrlsToLinks(product?.description)}
                    </div>

                    {/* Bulk Pricing Section */}
                    {product?.bulkPricing && 
                     product.bulkPricing.length > 0 && 
                     product.bulkPricing.some(item => item.quantity !== null && item.price !== null) && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-xl font-semibold text-gray-800">Bulk Pricing</h3>
                          <span className="px-2 py-0.5 bg-green-100 text-green-600 text-sm rounded-full">
                            Save More
                          </span>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                          <div className="p-3 border-b border-gray-100">
                            <p className="text-sm text-gray-600">
                              Buy more and save! Click on a bulk pricing option to automatically set the quantity and get the discounted price. Click again to deselect.
                            </p>
                          </div>
                          <div className="p-3">
                            <div className="space-y-2">
                              {product.bulkPricing
                                .filter(item => item.quantity !== null && item.price !== null && item.quantity > 1)
                                .sort((a, b) => a.quantity - b.quantity)
                                .map((item, index) => {
                                  // Use the perItemPrice from the backend data if available, otherwise calculate
                                  const perItemPrice = item.perItemPrice || (item.price / item.quantity);
                                  const savings = product.price - perItemPrice;
                                  const savingsPercentage = ((savings / product.price) * 100).toFixed(0);
                                  
                                  const isSelected = selectedBulkPricing && selectedBulkPricing.quantity === item.quantity;
                                  const isHighlighted = (typeof quantity === 'string' ? parseInt(quantity, 10) || 1 : quantity) >= item.quantity;
                                  
                                  return (
                                    <div 
                                      key={index} 
                                      onClick={() => handleBulkPricingSelect(item)}
                                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                                        isSelected
                                          ? 'border-indigo-500 bg-indigo-100 shadow-md' 
                                          : isHighlighted
                                          ? 'border-indigo-200 bg-indigo-50/50' 
                                          : 'border-gray-100 hover:border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isSelected && (
                                          <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                        )}
                                        <span className="text-sm font-medium text-gray-800">
                                          {item.quantity} {item.quantity === 1 ? 'item' : 'items'}
                                        </span>
                                        {isSelected && (
                                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full font-medium">
                                            Selected
                                          </span>
                                        )}
                                        {savings > 0 && !isSelected && (
                                          <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">
                                            Save {savingsPercentage}%
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <div className="text-sm text-indigo-600 font-semibold">
                                          {product.currency} {Number(item.price).toFixed(2)}
                                        </div>
                                        {savings > 0 && (
                                          <div className="text-xs text-gray-500">
                                            {product.currency} {Number(perItemPrice).toFixed(2)} per item
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Addons Section */}
                    {product?.addons && product.addons.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-xl font-bold text-gray-800">Add-ons</h3>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 text-sm font-medium rounded-full">
                              Optional
                            </span>
                            <span className="text-sm text-gray-500">
                              {product.addons.length} {product.addons.length === 1 ? 'addon' : 'addons'} available
                            </span>
                          </div>
                        </div>

                        <div className="grid gap-3">
                          {product.addons.map((addon, addonIndex) => (
                            <div key={addonIndex} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                              {/* Addon Header */}
                              <div className="p-3 border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-base font-semibold text-gray-800">{addon.question}</h4>
                                  <span className="text-sm text-gray-500">
                                    {addon.options.length} {addon.options.length === 1 ? 'option' : 'options'}
                                  </span>
                                </div>
                              </div>

                              {/* Addon Options */}
                              <div className="p-3">
                                {addon.type === "Drop-Down List" ? (
                                  <div className="relative">
                                    <select
                                      onChange={(e) => {
                                        const selectedOption = addon.options.find(opt => opt.text === e.target.value);
                                        handleAddonChange(addonIndex, selectedOption);
                                      }}
                                      className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all duration-200 appearance-none bg-white text-sm"
                                      defaultValue=""
                                    >
                                      <option value="" disabled>Select an option</option>
                                      {addon.options.map((option, optionIndex) => (
                                        <option key={optionIndex} value={option.text}>
                                          {option.text} {option.price > 0 ? `(+${product.currency} ${Number(option.price).toFixed(2)})` : '(Free)'}
                                        </option>
                                      ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                      </svg>
                                    </div>
                                  </div>
                                ) : addon.type === "Radio Buttons" ? (
                                  <div className="grid gap-2">
                                    {addon.options.map((option, optionIndex) => (
                                      <label
                                        key={optionIndex}
                                        className="relative flex items-start gap-3 p-2 rounded-lg border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/50 cursor-pointer transition-all duration-200"
                                      >
                                        <div className="flex items-center h-4 mt-0.5">
                                          <input
                                            type="radio"
                                            name={`addon-${addonIndex}`}
                                            value={option.text}
                                            onChange={() => handleAddonChange(addonIndex, option)}
                                            className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-3">
                                            <div>
                                              <span className="block text-sm font-medium text-gray-800">
                                                {option.text}
                                              </span>
                                            </div>
                                            <div className="flex-shrink-0">
                                              {option.price > 0 ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                                                  +{product.currency} {Number(option.price).toFixed(2)}
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                                  Free
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="grid gap-2">
                                    {addon.options.map((option, optionIndex) => (
                                      <label
                                        key={optionIndex}
                                        className="relative flex items-start gap-3 p-2 rounded-lg border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/50 cursor-pointer transition-all duration-200"
                                      >
                                        <div className="flex items-center h-4 mt-0.5">
                                          <input
                                            type="checkbox"
                                            name={`addon-${addonIndex}`}
                                            value={option.text}
                                            checked={Array.isArray(selectedAddons[addonIndex]) && selectedAddons[addonIndex].some(item => item.text === option.text)}
                                            onChange={() => handleAddonChange(addonIndex, option)}
                                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between gap-3">
                                            <div>
                                              <span className="block text-sm font-medium text-gray-800">
                                                {option.text}
                                              </span>
                                            </div>
                                            <div className="flex-shrink-0">
                                              {option.price > 0 ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                                                  +{product.currency} {Number(option.price).toFixed(2)}
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                                  Free
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Cumulative Total Display */}
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Base Price:</span>
                            <div className="flex items-center gap-2">
                              {selectedBulkPricing ? (
                                // When bulk pricing is selected, show the effective per-item bulk price as Base Price
                                (() => {
                                  const bulkUnit =
                                    selectedBulkPricing.quantity > 0
                                      ? selectedBulkPricing.price / selectedBulkPricing.quantity
                                      : selectedBulkPricing.price;
                                  return (
                                    <span className="text-sm text-gray-800">
                                      {product.currency} {Number(bulkUnit).toFixed(2)}
                                    </span>
                                  );
                                })()
                              ) : product.discount > 0 ? (
                                <>
                                  <span className="text-sm text-gray-500 line-through">
                                    {product.currency} {Number(product.price).toFixed(2)}
                                  </span>
                                  <span className="text-sm text-gray-800">
                                    {product.currency} {Number(product.price * (1 - product.discount / 100)).toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <span className="text-sm text-gray-800">
                                  {product.currency} {Number(product.price).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Selected Add-ons Breakdown (per unit × quantity) */}
                          {(() => {
                            const mult =
                              typeof bulkMultiplier === 'string'
                                ? parseInt(bulkMultiplier, 10) || 1
                                : bulkMultiplier;
                            const addonLineQty = selectedBulkPricing
                              ? mult * Number(selectedBulkPricing.quantity || 1)
                              : typeof quantity === 'string'
                                ? parseInt(quantity, 10) || 1
                                : quantity;

                            return Object.entries(selectedAddons).map(([addonIndex, addon]) => {
                              const fmtAddonLine = (unitPrice) => {
                                const u = Number(unitPrice) || 0;
                                if (addonLineQty <= 1) {
                                  return `+${product.currency} ${u.toFixed(2)}`;
                                }
                                return `+${product.currency} ${u.toFixed(2)} each × ${addonLineQty} = ${product.currency} ${(u * addonLineQty).toFixed(2)}`;
                              };

                              if (Array.isArray(addon)) {
                                return addon.map((option, index) => (
                                  <div key={`${addonIndex}-${index}`} className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-gray-600">+ {option.text}:</span>
                                    <span className="text-sm text-gray-800 text-right">{fmtAddonLine(option.price)}</span>
                                  </div>
                                ));
                              }
                              if (addon && addon.price > 0) {
                                return (
                                  <div key={addonIndex} className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-gray-600">+ {addon.text}:</span>
                                    <span className="text-sm text-gray-800 text-right">{fmtAddonLine(addon.price)}</span>
                                  </div>
                                );
                              }
                              return null;
                            });
                          })()}

                          {/* Total Price */}
                          <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                            <span className="text-base font-semibold text-gray-800">Total Price:</span>
                            <span className="text-base font-bold text-indigo-600">{product.currency} {calculateTotalPrice().toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}



                    {/* Quantity and Add to Cart */}
                    <div className="space-y-4 mb-6">
                      {/* Regular Quantity Controls - Only show when NO bulk pricing is selected */}
                      {!selectedBulkPricing && (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={dec}
                              className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                              -
                            </button>
                            <input
                              type="text"
                              value={quantity}
                              onChange={handleQuantityChange}
                              onBlur={handleQuantityBlur}
                              className="w-12 h-10 text-center text-gray-800 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="1"
                            />
                            <button
                              onClick={inc}
                              className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                              +
                            </button>
                          </div>
                          <button
                            onClick={handleAddToCart}
                            className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium"
                          >
                            Add to Cart
                          </button>
                          <button
                            onClick={add_wishlist}
                            className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                          >
                            <AiFillHeart className="text-red-500 text-xl" />
                          </button>
                        </div>
                      )}
                      
                      {/* Bulk Multiplier Section - Only show when bulk pricing is selected */}
                      {selectedBulkPricing && (
                        <div className="space-y-3">
                          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm text-indigo-700 font-medium whitespace-nowrap">
                                  How many {selectedBulkPricing.quantity}-packs?
                                </div>
                                <input
                                  type="text"
                                  value={bulkMultiplier}
                                  onChange={handleBulkMultiplierChange}
                                  onBlur={handleBulkMultiplierBlur}
                                  className="w-16 h-8 text-center text-indigo-800 border border-indigo-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  placeholder="1"
                                />
                                <div className="text-xs text-indigo-600 whitespace-nowrap">
                                  = {(() => {
                                    const multiplier = typeof bulkMultiplier === 'string' ? parseInt(bulkMultiplier, 10) || 1 : bulkMultiplier;
                                    return multiplier * selectedBulkPricing.quantity;
                                  })()} total items
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedBulkPricing(null);
                                    setBulkMultiplier(1);
                                    setQuantity(1);
                                    const newTotalPrice = calculateTotalPrice();
                                    setCurrentPrice(newTotalPrice);
                                  }}
                                  className="px-3 py-1 text-xs bg-white text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50 transition-colors duration-200 whitespace-nowrap"
                                  title="Return to regular quantity selection"
                                >
                                  Clear Bulk
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <button
                              onClick={handleAddToCart}
                              className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium"
                            >
                              Add to Cart
                            </button>
                            <button
                              onClick={add_wishlist}
                              className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                              <AiFillHeart className="text-red-500 text-xl" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contact Seller Buttons */}
                    <div className="flex gap-4 mb-6">
                    <a
                      href={`https://wa.me/${formatPhoneNumber(supportInfo?.mobileNo)}?text=Hi, I'm interested in your product: ${product.name} (${window.location.href})`}
                      target="_blank"
                      rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-lg hover:bg-[#128C7E] transition-colors duration-200 font-medium"
                    >
                      <FaWhatsapp className="text-xl" />
                      WhatsApp
                    </a>
                    </div>

                    {/* Social Share */}
                    <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                      <span className="text-gray-600">Share:</span>
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => handleShare('facebook')}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          <FaFacebookF className="text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleShare('twitter')}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          <AiOutlineTwitter className="text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleShare('linkedin')}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          <FaLinkedin className="text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleShare('whatsapp')}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          <FaWhatsapp className="text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleShare('copy')}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          <FaShareAlt className="text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Details Tabs */}
              <div className="mt-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="flex border-b border-gray-100">
                      <button
                        onClick={() => setState("reviews")}
                      className={`px-6 py-4 text-sm font-medium ${
                          state === "reviews"
                          ? "text-indigo-600 border-b-2 border-indigo-600"
                          : "text-gray-600 hover:text-indigo-600"
                      }`}
                      >
                        Reviews
                      </button>
                      <button
                        onClick={() => setState("description")}
                      className={`px-6 py-4 text-sm font-medium ${
                          state === "description"
                          ? "text-indigo-600 border-b-2 border-indigo-600"
                          : "text-gray-600 hover:text-indigo-600"
                      }`}
                      >
                        Description
                      </button>
                    </div>
                  <div className="p-6">
                      {state === "reviews" ? (
                      <Reviews 
                        productId={product?._id} 
                        product={product}
                        sellerId={sellerId}
                      />
                      ) : (
                      <div className="prose prose-lg max-w-none">
                        <div className="whitespace-pre-line">
                          {convertUrlsToLinks(product?.description)}
                        </div>
                    </div>
                    )}
                  </div>
                </div>
                    </div>

              {/* Related Products */}
              {relatedProducts && relatedProducts.filter(p => parseInt(p?.packSize) === 1).length > 0 && (
                <div className="mt-8">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-6">Related Products</h2>
                  <div className="grid grid-cols-4 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
                    {relatedProducts
                      .filter(p => parseInt(p?.packSize) === 1)
                      .map((p, i) => (
                      <Link
                        key={i}
                        className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                        to={`/${sellerName}/product/details/${p.slug}`}
                      >
                        <div className="relative w-full h-[200px] overflow-hidden">
                          <img
                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                            src={getProxiedUrl(p.images[0])}
                            alt={p.name}
                          />
                          {p.discount > 0 && (
                            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                              -{p.discount}% OFF
                                </div>
                              )}
                            </div>
                        <div className="p-4">
                          <h2 className="text-gray-800 font-semibold mb-2 line-clamp-2 text-sm group-hover:text-indigo-600 transition-colors duration-200">
                            {p.name}
                              </h2>
                          <div className="flex items-center gap-2 mb-2">
                            {p.discount > 0 ? (
                              <>
                                <span className="text-base font-bold text-indigo-600">
                                  {p.currency} {Number(p.price * (1 - p.discount / 100)).toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-500 line-through">
                                  {p.currency} {Number(p.price).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span className="text-base font-bold text-indigo-600">
                                {p.currency} {Number(p.price).toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">View Details →</span>
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-400 text-xs">★</span>
                              <span className="text-xs text-gray-600">{p.rating || 0}</span>
                            </div>
                              </div>
                            </div>
                          </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Footer sellerId={sellerId} />
        </>
      )}
    </div>
  );
};

export default Details;
